import {
  useState, useMemo,
} from 'react';
import {
  X, Search, Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type {
  CatalogItem, RelatedItem 
} from '../../types';
import {
  RELATIONSHIP_TYPE_LABELS,
  RELATIONSHIP_TYPE_COLORS,
  STORAGE_TYPE_LABELS,
} from '../../types';

type RelationshipType = 'substitute' | 'similar' | 'component' | 'parent';

interface AddRelatedItemModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (relatedItem: RelatedItem) => void;
  readonly currentItem: CatalogItem;
  readonly catalogItems: CatalogItem[];
  readonly existingRelatedIds: string[];
}

const RELATIONSHIP_TYPES: RelationshipType[] = ['substitute', 'similar', 'component', 'parent'];

export function AddRelatedItemModal({
  isOpen,
  onClose,
  onSave,
  currentItem,
  catalogItems,
  existingRelatedIds,
}: AddRelatedItemModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [relationshipType, setRelationshipType] = useState<RelationshipType>('substitute');
  const [conversionFactor, setConversionFactor] = useState('1');
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const availableItems = useMemo(() => {
    return catalogItems.filter((item) => {
      // Exclude current item and already related items
      if (item.itemId === currentItem.itemId) return false;
      if (existingRelatedIds.includes(item.itemId)) return false;

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(query) ||
          item.sku.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [catalogItems, currentItem.itemId, existingRelatedIds, searchQuery]);

  const handleSave = () => {
    if (!selectedItem) return;

    const relatedItem: RelatedItem = {
      itemId: selectedItem.itemId,
      sku: selectedItem.sku,
      name: selectedItem.name,
      relationshipType,
      conversionFactor: parseFloat(conversionFactor) || 1,
      notes: notes || undefined,
    };

    onSave(relatedItem);
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedItem(null);
    setRelationshipType('substitute');
    setConversionFactor('1');
    setNotes('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative z-10 flex h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <ModalHeader onClose={handleClose} />

        <div className="flex-1 overflow-hidden p-4">
          <div className="space-y-4">
            {/* Relationship Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Relationship Type
              </label>
              <div className="flex flex-wrap gap-2">
                {RELATIONSHIP_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setRelationshipType(type)}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                      relationshipType === type
                        ? RELATIONSHIP_TYPE_COLORS[type]
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    )}
                  >
                    {RELATIONSHIP_TYPE_LABELS[type]}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {getRelationshipDescription(relationshipType)}
              </p>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Catalog Items
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, SKU, or category..."
                  className="w-full rounded-md border py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Item List */}
            <div className="h-48 overflow-y-auto rounded-md border">
              {availableItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Package className="h-8 w-8 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No items found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {availableItems.slice(0, 50).map((item) => (
                    <ItemRow
                      key={item.itemId}
                      item={item}
                      isSelected={selectedItem?.itemId === item.itemId}
                      onSelect={() => setSelectedItem(item)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Selected Item Details */}
            {selectedItem && (
              <SelectedItemDetails
                item={selectedItem}
                relationshipType={relationshipType}
                conversionFactor={conversionFactor}
                setConversionFactor={setConversionFactor}
                notes={notes}
                setNotes={setNotes}
              />
            )}
          </div>
        </div>

        <ModalFooter
          onClose={handleClose}
          onSave={handleSave}
          isValid={Boolean(selectedItem)}
        />
      </div>
    </div>
  );
}

function getRelationshipDescription(type: RelationshipType): string {
  switch (type) {
    case 'substitute':
      return 'A direct replacement that can be used when the primary item is unavailable.';
    case 'similar':
      return 'A similar product that may be considered as an alternative.';
    case 'component':
      return 'This item is a component or ingredient of another item.';
    case 'parent':
      return 'This item is the parent or base product.';
    default:
      return '';
  }
}

interface ModalHeaderProps {readonly onClose: () => void;}

function ModalHeader({ onClose }: ModalHeaderProps) {
  return (
    <div className="flex items-center justify-between border-b p-4">
      <h2 className="text-lg font-semibold">Add Related Item</h2>
      <button
        type="button"
        onClick={onClose}
        className="rounded-full p-1 hover:bg-gray-100"
        aria-label="Close modal"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

interface ItemRowProps {
  readonly item: CatalogItem;
  readonly isSelected: boolean;
  readonly onSelect: () => void;
}

function ItemRow({
  item, isSelected, onSelect 
}: ItemRowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full px-4 py-3 text-left transition-colors',
        isSelected ? 'bg-primary/10' : 'hover:bg-gray-50'
      )}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={cn('font-medium', isSelected && 'text-primary')}>
            {item.name}
          </p>
          <p className="text-sm text-gray-500">
            SKU: {item.sku} • {item.category} • {STORAGE_TYPE_LABELS[item.storageType]}
          </p>
        </div>
        {isSelected && (
          <div className="h-4 w-4 rounded-full bg-primary" />
        )}
      </div>
    </button>
  );
}

interface SelectedItemDetailsProps {
  readonly item: CatalogItem;
  readonly relationshipType: RelationshipType;
  readonly conversionFactor: string;
  readonly setConversionFactor: (value: string) => void;
  readonly notes: string;
  readonly setNotes: (value: string) => void;
}

function SelectedItemDetails({
  item,
  relationshipType,
  conversionFactor,
  setConversionFactor,
  notes,
  setNotes,
}: SelectedItemDetailsProps) {
  const showConversionFactor = relationshipType === 'substitute' || relationshipType === 'component';

  return (
    <div className="rounded-lg border bg-gray-50 p-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-white p-2 shadow-sm">
          <Package className="h-5 w-5 text-gray-600" />
        </div>
        <div>
          <p className="font-medium text-gray-900">{item.name}</p>
          <p className="text-sm text-gray-500">SKU: {item.sku}</p>
        </div>
      </div>

      {showConversionFactor && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Conversion Factor
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={conversionFactor}
            onChange={(e) => setConversionFactor(e.target.value)}
            className="w-32 rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-gray-500">
            How many units of this item equal 1 unit of the original
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any additional notes about this relationship..."
          rows={2}
          className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
    </div>
  );
}

interface ModalFooterProps {
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly isValid: boolean;
}

function ModalFooter({
  onClose, onSave, isValid 
}: ModalFooterProps) {
  return (
    <div className="flex justify-end gap-3 border-t p-4">
      <Button variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button onClick={onSave} disabled={!isValid}>
        Add Related Item
      </Button>
    </div>
  );
}
